import { MessageService } from 'primeng/api';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { FilterService } from '@modules/search/services/filter.service';
import { TfaType } from '@modules/settings/components/manage-users/interfaces/interfaces';
import { appConfig } from '@shared/interfaces/interfaces';
import { UserResponse, PaginatedUserListResponse, ProjectUserListParams } from '@shared/interfaces/users.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: HttpClient, private router: Router, private messageService: MessageService) {}
  isLoggingIn: boolean = false;

  appConfig: WritableSignal<appConfig> = signal({} as appConfig);
  getUserDetails(): Observable<UserResponse> {
    return this.http.get<UserResponse>('/api/users/me');
  }

  getAllUsers(params: any = {}): Observable<object> {
    return this.http.get('/api/users', params);
  }

  getAllUserList(params: any = {}): Observable<object> {
    return this.http.get('/api/users/all', params);
  }

  /**
   * Get paginated and filtered user list for the current project
   * @param options - Query parameters for filtering and pagination
   * @param options.q - Search query to filter by email, firstName, or lastName
   * @param options.page - Page number (default: 1)
   * @param options.limit - Number of items per page (default: 10)
   * @param options.role - Filter by user role (e.g., 'user', 'manager', 'admin')
   * @returns Observable of paginated user list response
   */
  getProjectUserList(options: ProjectUserListParams = {}): Observable<PaginatedUserListResponse> {
    let params = new HttpParams();

    if (options.q !== undefined && options.q !== null) {
      params = params.set('q', options.q);
    }

    if (options.page !== undefined) {
      params = params.set('page', options.page.toString());
    }

    if (options.limit !== undefined) {
      params = params.set('limit', options.limit.toString());
    }

    if (options.role !== undefined && options.role !== null) {
      if (Array.isArray(options.role)) {
        // Add multiple role parameters: role=user&role=manager
        options.role.forEach(r => {
          params = params.append('role', r);
        });
      } else {
        // Single role parameter
        params = params.set('role', options.role);
      }
    }

    return this.http.get<PaginatedUserListResponse>('/api/users/list', { params });
  }

  getAppConfig(): Observable<object> {
    return this.http.get('/api/auth/app-configurations');
  }

  updateAppConfig(payload: any): Observable<object> {
    return this.http.post('/api/auth/app-configurations/add', payload);
  }
  userDetail: WritableSignal<UserResponse> = signal({} as UserResponse);
  userList: WritableSignal<UserResponse[]> = signal([] as UserResponse[]);
  userByEmail: { [email: string]: UserResponse } = {};
  idpDetails: any = signal(null);

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  }

  tfaConfig: WritableSignal<TfaType> = signal({} as TfaType);

  getJwtToken(params: any = []): Observable<object> {
    return this.http.get('/api/auth/sso/login/token', { params });
  }

  getJWTLocalStorage(): string {
    return localStorage.getItem('token') || '';
  }

  setJwtTokenAndRedirect(token: string): void {
    localStorage.setItem('token', token);
    setTimeout(() => {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate(['/']);
      });
    }, 100);
  }

  setJwtToken(token: string): void {
    localStorage.setItem('token', token);
  }

  logout(): void {
    if(this.isAuthenticated()) {
      this.logoutUser({id: this.userDetail()._id}).subscribe(()=>{});
    }
    this.userDetail.set({} as UserResponse);
    localStorage.removeItem('token');
    window.location.href = '/login';
  }

  syncLogout(): void {
    this.userDetail.set({} as UserResponse);
    if (localStorage.getItem('token')) {
      localStorage.removeItem('token');
    }
    window.location.href = '/login';
  }

  logoutUser(body: { id: string } = { id: this.userDetail()._id }): Observable<object> {
    return this.http.post('/api/auth/logout', body);
  }

  checkProvider(params: any = {}): Observable<object> {
    return this.http.get('/api/auth/check-provider', params);
  }

  login(params: any = {}, body = {}, otp: string = ''): Observable<object> {
    return this.http.post('/api/auth/login', body, {
      params,
      headers: {
        'X-tfa': `${otp}`,
      },
    });
  }

  challenge(body: any = {}): Observable<object> {
    return this.http.post('/api/auth/challenge', body);
  }

  createPassword(params: any = {}, body = {}): Observable<object> {
    return this.http.put(`/api/users/create-password/${params.id}`, body);
  }

  updatePassword(params: any = {}, body = {}): Observable<object> {
    return this.http.put(`/api/users/update-password/${params.id}`, body);
  }

  updateSalt(body = {}): any {
    return this.http.put(`/api/users/salt`, body).toPromise();
  }

  getSalt = (params: any = {}): any => {
    return this.http.get(`/api/users/salt/${params._id}`).toPromise();
  };

  setUserByEmail = (userList: UserResponse[]) => {
    this.userByEmail = userList.reduce(
      (acc: { [email: string]: UserResponse }, user: UserResponse) => {
        acc[user.email.toLowerCase().trim()] = user;
        return acc;
      },
      {}
    );
  };

  forgotPassword(body = {}): Observable<object> {
    return this.http.post('/api/users/reset-password/', body);
  }

  updateUserDetails(params: { _id: string }, body = {}): Observable<object> {
    return this.http.put(`/api/users/${params?._id}`, body);
  }

  activateUser(
    user: string,
    otp: string,
    password: string
  ): Observable<object> {
    return this.http.put(
      `/api/users/activate-user/${user}`,
      { password },
      {
        headers: {
          'X-tfa': otp,
        },
      }
    );
  }

  getTFAData(user: string): Observable<TfaType> {
    return this.http.get<TfaType>(`/api/auth/tfa/get/${user}`);
  }

  signTFA(user: string): Observable<object> {
    return this.http.post(`/api/auth/tfa/setup/${user}`, {});
  }

  verifyTFA(user: string, otp: string): Observable<object> {
    return this.http.post(
      `/api/auth/tfa/verify/${user}`,
      {},
      {
        headers: {
          'X-tfa': otp,
        },
      }
    );
  }

  isTFAconfigured(user: string): Observable<object> {
    return this.http.get(`/api/auth/tfa/get/${user}`);
  }

  deleteTFA(user: string): Observable<object> {
    return this.http.delete(`/api/auth/tfa/delete/${user}`, {});
  }
}
